// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Contextual Tutorial System
 * Shows hints when relevant instead of 11-step tutorial
 */

import STORAGE_KEYS from '../system/STORAGE_KEYS';

class ContextualTutorial {
  constructor() {
    this.hints = {
      firstWarp: {
        id: 'firstWarp',
        title: 'Warp Travel',
        message: 'Select a system on the map, then click SET COURSE to travel. Higher warp factors are faster but use more fuel.',
        icon: '🚀',
        trigger: 'beforeFirstWarp',
        shown: false
      },
      firstCombat: {
        id: 'firstCombat',
        title: 'Combat Encounter',
        message: 'Target enemies and choose fire modes (SINGLE/BURST/SALVO) for tactical advantage. Watch your power levels!',
        icon: '⚔️',
        trigger: 'onCombatStart',
        shown: false
      },
      firstOrbit: {
        id: 'firstOrbit',
        title: 'Docked at Station',
        message: 'You can refuel, trade, upgrade your ship, and hire crew. Explore all the options!',
        icon: '🛸',
        trigger: 'onFirstOrbit',
        shown: false
      },
      lowFuel: {
        id: 'lowFuel',
        title: 'Low Fuel Warning',
        message: 'Fuel below 20%! Find a station to refuel, or use emergency options (distress beacon, fuel synthesis).',
        icon: '⚠️',
        trigger: 'onLowFuel',
        shown: false,
        repeatable: true
      },
      firstMission: {
        id: 'firstMission',
        title: 'Mission Available',
        message: 'Missions earn credits and reputation. Check the MISSIONS tab to accept one.',
        icon: '📋',
        trigger: 'onFirstMissionAvailable',
        shown: false
      },
      firstAlien: {
        id: 'firstAlien',
        title: 'Alien Encounter',
        message: 'You\'ve encountered alien life! Scan planets to discover new species.',
        icon: '👽',
        trigger: 'onFirstAlienEncounter',
        shown: false
      },
      firstRankUp: {
        id: 'firstRankUp',
        title: 'Rank Progression',
        message: 'You\'re earning rank progress! Visit systems, win combat, and complete missions to rank up.',
        icon: '🎖️',
        trigger: 'onFirstRankProgress',
        shown: false
      },
      quickActions: {
        id: 'quickActions',
        title: 'Quick Navigation',
        message: 'Use keyboard shortcuts to switch tabs: N (Nav), H (Ship), C (Crew), G (Cargo), M (Missions), T (Stats), O (More).',
        icon: '⚡',
        trigger: 'afterFirstOrbit',
        shown: false
      }
    };
    
    this.load();
  }

  load() {
    const saved = localStorage.getItem(STORAGE_KEYS.TUTORIAL_HINTS);
    if (saved) {
      const shownHints = JSON.parse(saved);
      Object.keys(shownHints).forEach(id => {
        if (this.hints[id]) {
          this.hints[id].shown = shownHints[id];
        }
      });
    }
  }

  save() {
    const shownHints = {};
    Object.values(this.hints).forEach(hint => {
      shownHints[hint.id] = hint.shown;
    });
    localStorage.setItem(STORAGE_KEYS.TUTORIAL_HINTS, JSON.stringify(shownHints));
  }

  shouldShow(triggerId) {
    const hint = Object.values(this.hints).find(h => h.trigger === triggerId);
    if (!hint) return null;
    
    if (hint.repeatable || !hint.shown) {
      return hint;
    }
    
    return null;
  }

  markShown(hintId) {
    if (this.hints[hintId]) {
      this.hints[hintId].shown = true;
      this.save();
    }
  }

  reset() {
    Object.values(this.hints).forEach(hint => {
      hint.shown = false;
    });
    this.save();
  }

  getProgress() {
    const total = Object.values(this.hints).filter(h => !h.repeatable).length;
    const shown = Object.values(this.hints).filter(h => !h.repeatable && h.shown).length;
    return { shown, total, percentage: Math.round((shown / total) * 100) };
  }
}

// Singleton instance
const contextualTutorial = new ContextualTutorial();

export default contextualTutorial;
